import { useCallback, useEffect, useMemo, useState } from 'react';
import { relevamientoService, RelevamientoNotificacion } from '../services/relevamientoService';
import { useCenter } from '../contexts/CenterContext';
import { usePermissionsContext } from '../contexts/PermissionsContext';
import {
  loadReadIds,
  markIdsRead,
  markIdsUnread,
  NOTIFICATIONS_POLL_MS,
  NOTIFICATIONS_TAKE,
  READ_STORAGE_KEY,
  READ_SYNC_EVENT,
  RELEVAMIENTO_VIEW_PERMS,
} from '../utils/relevamientoNotifications';

export function useRelevamientoNotifications() {
  const { currentCenter } = useCenter();
  const { loading: permissionsLoading, hasAny } = usePermissionsContext();
  const canViewRelevamientos = hasAny(RELEVAMIENTO_VIEW_PERMS);

  const [items, setItems] = useState<RelevamientoNotificacion[]>([]);
  const [readIds, setReadIds] = useState<Set<number>>(() => loadReadIds());
  const [loading, setLoading] = useState(false);

  const refreshReadIds = useCallback(() => {
    setReadIds(loadReadIds());
  }, []);

  const load = useCallback(async () => {
    if (!canViewRelevamientos) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      const data = await relevamientoService.getNotificaciones(NOTIFICATIONS_TAKE);
      setItems(data);
      refreshReadIds();
    } catch (error) {
      console.error('Error cargando notificaciones de relevamientos:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [canViewRelevamientos, refreshReadIds]);

  useEffect(() => {
    if (permissionsLoading || !canViewRelevamientos) {
      setItems([]);
      return;
    }
    load();
    const timer = window.setInterval(load, NOTIFICATIONS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [load, currentCenter?.id, permissionsLoading, canViewRelevamientos]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === READ_STORAGE_KEY || event.key === null) {
        refreshReadIds();
      }
    };
    const onSync = () => refreshReadIds();
    window.addEventListener('storage', onStorage);
    window.addEventListener(READ_SYNC_EVENT, onSync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(READ_SYNC_EVENT, onSync);
    };
  }, [refreshReadIds]);

  const unreadCount = useMemo(
    () => items.filter((item) => !readIds.has(item.id)).length,
    [items, readIds],
  );

  const markRead = useCallback((ids: number[]) => {
    setReadIds(markIdsRead(ids));
  }, []);

  const markUnread = useCallback((ids: number[]) => {
    setReadIds(markIdsUnread(ids));
  }, []);

  const isRead = useCallback((id: number) => readIds.has(id), [readIds]);

  return {
    items,
    loading,
    permissionsLoading,
    canViewRelevamientos,
    unreadCount,
    isRead,
    markRead,
    markUnread,
    refresh: load,
  };
}
